import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ActivityTracker } from './components/common/ActivityTracker';
import { ToastProvider } from './components/common/Toast';
import { CookieConsentBanner } from './components/Legal/CookieConsent';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { LoadingSpinner } from './components/Loading/LoadingSpinner';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { CleverCallback } from './components/auth/CleverCallback';
import { AcceptTerms } from './components/auth/AcceptTerms';
import { AcceptTeacherInvite } from './components/auth/AcceptTeacherInvite';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { LandingPage } from './components/Landing/LandingPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RoleGuard } from './components/auth/RoleGuard';
import { FeatureGate } from './components/common/FeatureGate';
import { sessionSecurity } from './utils/sessionSecurity';
import { NotFound } from './components/NotFound/NotFound';
import { SkipLinks } from './components/Accessibility/SkipLinks';
// S132: both were written and neither was mounted. `GlobalShortcuts` binds every
// chord the shortcuts dialog advertises; `KeyboardShortcutsOverlay` IS that
// dialog, and was the only place '?' was ever registered — so until now it could
// not be opened at all, by key or by button.
import { GlobalShortcuts } from './components/Accessibility/GlobalShortcuts';
import { KeyboardShortcutsOverlay } from './components/Accessibility/KeyboardShortcutsOverlay';
import { TermsOfService } from './components/Legal/TermsOfService';
import { PrivacyPolicy } from './components/Legal/PrivacyPolicy';
import { EmailVerificationBanner } from './components/EmailVerification/EmailVerification';
import { logger } from './utils/logger';
import { initializeAllServices } from './config/firebase';
import './App.css';
import './styles/accessibility.css';
// RTL layout. Imported 2026-09-05 — the stylesheet has existed since Arabic
// was added to the language picker and was never loaded, so selecting Arabic
// set `dir="rtl"` on the document and nothing responded to it: the whole UI
// stayed left-aligned with the text mirrored inside it. Every rule in here is
// scoped to `[dir='rtl']`, so it costs an LTR user nothing.
import './styles/rtl.css';

// Lazy load heavy components with optimized imports
const Dashboard = lazy(() => 
  import(/* webpackChunkName: "dashboard" */ './components/Dashboard/Dashboard')
    .then(module => ({ default: module.Dashboard }))
);
const NewProject = lazy(() => 
  import(/* webpackChunkName: "new-project" */ './components/NewProject/NewProject')
    .then(module => ({ default: module.NewProject }))
);
const ProjectEditor = lazy(() => 
  import(/* webpackChunkName: "project-editor" */ './components/ProjectEditor/ProjectEditor')
    .then(module => ({ default: module.ProjectEditor }))
);
const Settings = lazy(() =>
  import(/* webpackChunkName: "settings" */ './components/Settings/Settings')
    .then(module => ({ default: module.Settings }))
);
const PlayGame = lazy(() =>
  import(/* webpackChunkName: "play-game" */ './components/PlayGame/PlayGame')
    .then(module => ({ default: module.PlayGame }))
);
const Achievements = lazy(() =>
  import(/* webpackChunkName: "achievements" */ './components/Achievements/Achievements')
    .then(module => ({ default: module.Achievements }))
);
const CurriculumBrowser = lazy(() =>
  import(/* webpackChunkName: "curriculum" */ './components/Curriculum/CurriculumBrowser')
    .then(module => ({ default: module.CurriculumBrowser }))
);
const ChallengesHub = lazy(() =>
  import(/* webpackChunkName: "challenges-hub" */ './components/Challenges/ChallengesHub')
    .then(module => ({ default: module.ChallengesHub }))
);
const ChallengePlay = lazy(() =>
  import(/* webpackChunkName: "challenge-play" */ './components/Challenges/ChallengePlay')
    .then(module => ({ default: module.ChallengePlay }))
);
const MySkillsView = lazy(() =>
  import(/* webpackChunkName: "learning-path" */ './components/Skills/MySkillsView')
    .then(module => ({ default: module.MySkillsView }))
);
const GlossaryPage = lazy(() =>
  import(/* webpackChunkName: "glossary" */ './components/Glossary/GlossaryPage')
    .then(module => ({ default: module.GlossaryPage }))
);
const SnippetLibrary = lazy(() =>
  import(/* webpackChunkName: "snippets" */ './components/Snippets/SnippetLibrary')
    .then(module => ({ default: module.SnippetLibrary }))
);

// Tier 2: Teacher & Classroom Tools
const TeacherDashboard = lazy(() =>
  import(/* webpackChunkName: "teacher-dashboard" */ './components/Teacher/TeacherDashboard')
    .then(module => ({ default: module.TeacherDashboard }))
);
const ClassroomDetail = lazy(() =>
  import(/* webpackChunkName: "classroom-detail" */ './components/Teacher/ClassroomDetail')
    .then(module => ({ default: module.ClassroomDetail }))
);
const GradingView = lazy(() =>
  import(/* webpackChunkName: "grading-view" */ './components/Teacher/GradingView')
    .then(module => ({ default: module.GradingView }))
);
const AdminDashboard = lazy(() =>
  import(/* webpackChunkName: "admin-dashboard" */ './components/Admin/AdminDashboard')
    .then(module => ({ default: module.AdminDashboard }))
);
const LMSIntegration = lazy(() =>
  import(/* webpackChunkName: "lms-integration" */ './components/LMS/LMSIntegration')
    .then(module => ({ default: module.LMSIntegration }))
);
const StudentAssignments = lazy(() =>
  import(/* webpackChunkName: "student-assignments" */ './components/Student/StudentAssignments')
    .then(module => ({ default: module.StudentAssignments }))
);

// Tier 3: Student Progress & Assessment
const SkillMap = lazy(() =>
  import(/* webpackChunkName: "skill-map" */ './components/Skills/SkillMap')
    .then(module => ({ default: module.SkillMap }))
);
const AssessmentPlayer = lazy(() =>
  import(/* webpackChunkName: "assessment" */ './components/Assessment/AssessmentPlayer')
    .then(module => ({ default: module.AssessmentPlayer }))
);
const StudentReport = lazy(() =>
  import(/* webpackChunkName: "student-report" */ './components/Reports/StudentReport')
    .then(module => ({ default: module.StudentReport }))
);

// Tier 5: Community & Engagement
const ProjectGallery = lazy(() =>
  import(/* webpackChunkName: "gallery" */ './components/Gallery/ProjectGallery')
    .then(module => ({ default: module.ProjectGallery }))
);
const PeerReviewHub = lazy(() =>
  import(/* webpackChunkName: "peer-review" */ './components/PeerReview/PeerReviewHub')
    .then(module => ({ default: module.PeerReviewHub }))
);
const GameJamHub = lazy(() =>
  import(/* webpackChunkName: "game-jam" */ './components/GameJam/GameJamHub')
    .then(module => ({ default: module.GameJamHub }))
);
const LevelProgress = lazy(() =>
  import(/* webpackChunkName: "level-progress" */ './components/Leveling/LevelProgress')
    .then(module => ({ default: module.LevelProgress }))
);
const DailyChallenge = lazy(() =>
  import(/* webpackChunkName: "daily-challenge" */ './components/Leveling/DailyChallenge')
    .then(module => ({ default: module.DailyChallenge }))
);

// Tier 6: Debugging & Development Tools
const VersionHistoryPanel = lazy(() =>
  import(/* webpackChunkName: "version-history" */ './components/VersionHistory/VersionHistoryPanel')
    .then(module => ({ default: module.VersionHistoryPanel }))
);

// Tier 7: Content & Asset Tools
const SpriteLibrary = lazy(() =>
  import(/* webpackChunkName: "sprite-library" */ './components/Assets/SpriteLibrary')
    .then(module => ({ default: module.SpriteLibrary }))
);
const SoundLibrary = lazy(() =>
  import(/* webpackChunkName: "sound-library" */ './components/Assets/SoundLibrary')
    .then(module => ({ default: module.SoundLibrary }))
);
const SpriteEditor = lazy(() =>
  import(/* webpackChunkName: "sprite-editor" */ './components/Assets/SpriteEditor')
    .then(module => ({ default: module.SpriteEditor }))
);
const GuidedTemplateBuilder = lazy(() =>
  import(/* webpackChunkName: "template-builder" */ './components/Templates/GuidedTemplateBuilder')
    .then(module => ({ default: module.GuidedTemplateBuilder }))
);

// Tier 8: Advanced Capabilities
const PhaserWorkspace = lazy(() =>
  import(/* webpackChunkName: "phaser" */ './components/Phaser/PhaserWorkspace')
    .then(module => ({ default: module.PhaserWorkspace }))
);
const BlockEditor = lazy(() =>
  import(/* webpackChunkName: "block-editor" */ './components/BlockEditor/BlockEditor')
    .then(module => ({ default: module.BlockEditor }))
);
const CollaborationPanel = lazy(() =>
  import(/* webpackChunkName: "collaboration" */ './components/Collaboration/CollaborationPanel')
    .then(module => ({ default: module.CollaborationPanel }))
);
const ExportPanel = lazy(() =>
  import(/* webpackChunkName: "export" */ './components/Export/ExportPanel')
    .then(module => ({ default: module.ExportPanel }))
);
const ShortcutSettings = lazy(() =>
  import(/* webpackChunkName: "shortcuts" */ './components/Shortcuts/ShortcutSettings')
    .then(module => ({ default: module.ShortcutSettings }))
);

// Tier 9: Parental & Family Engagement
const ParentDashboard = lazy(() =>
  import(/* webpackChunkName: "parent-dashboard" */ './components/Parent/ParentDashboard')
    .then(module => ({ default: module.ParentDashboard }))
);
const ScreenTimeControls = lazy(() =>
  import(/* webpackChunkName: "screen-time" */ './components/Parent/ScreenTimeControls')
    .then(module => ({ default: module.ScreenTimeControls }))
);
const FamilyMode = lazy(() =>
  import(/* webpackChunkName: "family-mode" */ './components/Parent/FamilyMode')
    .then(module => ({ default: module.FamilyMode }))
);

// Tier 10: Platform & Infrastructure
const BillingDashboard = lazy(() =>
  import(/* webpackChunkName: "billing" */ './components/Billing/BillingDashboard')
    .then(module => ({ default: module.BillingDashboard }))
);
const TenantManager = lazy(() =>
  import(/* webpackChunkName: "tenant-manager" */ './components/Admin/TenantManager')
    .then(module => ({ default: module.TenantManager }))
);

// MOUNTED 2026-09-05. CMSEditor's own header said "Route: /admin/cms (wire in
// App.tsx if not already)" — it was not, and never had been. `cmsService` is
// read by HelpCenter, so the help content was Firestore-backed with no way for
// an admin to author it: the "edit content without a deploy" promise resolved
// to editing content with a deploy.
const CMSEditor = lazy(() =>
  import(/* webpackChunkName: "cms-editor" */ './components/Admin/CMSEditor')
    .then(module => ({ default: module.CMSEditor }))
);

// Tier 11: Documentation, Training & Support
const TeacherPDCourse = lazy(() =>
  import(/* webpackChunkName: "pd-course" */ './components/Training/TeacherPDCourse')
    .then(module => ({ default: module.TeacherPDCourse }))
);
const HelpCenter = lazy(() =>
  import(/* webpackChunkName: "help-center" */ './components/Help/HelpCenter')
    .then(module => ({ default: module.HelpCenter }))
);
const LessonPlanLibrary = lazy(() =>
  import(/* webpackChunkName: "lesson-plans" */ './components/Help/LessonPlanLibrary')
    .then(module => ({ default: module.LessonPlanLibrary }))
);
const GuidedOnboarding = lazy(() =>
  import(/* webpackChunkName: "onboarding" */ './components/Onboarding/GuidedOnboarding')
    .then(module => ({ default: module.GuidedOnboarding }))
);

// Tier 12: Internationalization
const LanguageSelector = lazy(() =>
  import(/* webpackChunkName: "language-selector" */ './components/Settings/LanguageSelector')
    .then(module => ({ default: module.LanguageSelector }))
);

// Tier 13: Cross-Curricular Integration
const MathVisualizer = lazy(() =>
  import(/* webpackChunkName: "math-viz" */ './components/CrossCurricular/MathVisualizer')
    .then(module => ({ default: module.MathVisualizer }))
);
const PhysicsLab = lazy(() =>
  import(/* webpackChunkName: "physics-lab" */ './components/CrossCurricular/PhysicsLab')
    .then(module => ({ default: module.PhysicsLab }))
);
const DesignStudio = lazy(() =>
  import(/* webpackChunkName: "design-studio" */ './components/CrossCurricular/DesignStudio')
    .then(module => ({ default: module.DesignStudio }))
);
const StoryBuilder = lazy(() =>
  import(/* webpackChunkName: "story-builder" */ './components/CrossCurricular/StoryBuilder')
    .then(module => ({ default: module.StoryBuilder }))
);

// Tier 14: Polish & UX
const NotificationCenter = lazy(() =>
  import(/* webpackChunkName: "notifications" */ './components/Notifications/NotificationCenter')
    .then(module => ({ default: module.NotificationCenter }))
);
const PresentationMode = lazy(() =>
  import(/* webpackChunkName: "presentation" */ './components/Presentation/PresentationMode')
    .then(module => ({ default: module.PresentationMode }))
);
const RecordingControls = lazy(() =>
  import(/* webpackChunkName: "recording" */ './components/Recording/RecordingControls')
    .then(module => ({ default: module.RecordingControls }))
);

// Tier 4: Safety, Compliance & Accessibility (continued — see Tier 4 block above)
const ParentalConsentFlow = lazy(() =>
  import(/* webpackChunkName: "parental-consent" */ './components/Safety/ParentalConsentFlow')
    .then(module => ({ default: module.ParentalConsentFlow }))
);
const AccessibilityStatement = lazy(() =>
  import(/* webpackChunkName: "accessibility-statement" */ './components/Accessibility/AccessibilityStatement')
    .then(module => ({ default: module.AccessibilityStatement }))
);
const AccessibilityPanel = lazy(() =>
  import(/* webpackChunkName: "accessibility-panel" */ './components/Accessibility/AccessibilityPanel')
    .then(module => ({ default: module.AccessibilityPanel }))
);

// Wrapper component to extract route params and load current code for VersionHistoryPanel
const VersionHistoryWrapper: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [currentCode, setCurrentCode] = useState<{ html: string; css: string; js: string }>({
    html: '',
    css: '',
    js: '',
  });

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    // Load the two heavy modules in parallel (was a nested import waterfall)
    // and guard against setState-after-unmount.
    (async () => {
      const [{ doc, getDoc }, { db }] = await Promise.all([
        import('firebase/firestore'),
        import('./config/firebase'),
      ]);
      const snap = await getDoc(doc(db.value, 'projects', projectId));
      if (!cancelled && snap.exists()) {
        const data = snap.data();
        setCurrentCode({
          html: data.html || '',
          css: data.css || '',
          js: data.js || '',
        });
      }
    })().catch((err) => logger.error('VersionHistoryWrapper load failed', err));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return <VersionHistoryPanel projectId={projectId || ''} currentCode={currentCode} />;
};

function App() {
  useEffect(() => {
    // Initialize session security monitoring
    logger.debug('Initializing session security and monitoring');
    
    // Initialize all Firebase services after first render to improve initial load time
    setTimeout(() => {
      initializeAllServices();
      logger.debug('All Firebase services initialized after first render');
    }, 100);
    
    // Session security is automatically initialized in its constructor.
    // #34 fix (2026-05-17): on invalid session, force sign-out instead of
    // only logging a warning. Previously a stale/expired session continued
    // operating until the user happened to refresh — a real security gap on
    // shared classroom devices. Calls the existing `secureLogout()` method
    // which clears sensitive state + invokes Firebase auth.signOut().
    const sessionCheckInterval = setInterval(() => {
      if (!sessionSecurity.isSessionValid()) {
        logger.warn('Session validation failed — forcing secure logout');
        try {
          sessionSecurity.secureLogout();
        } catch (err) {
          logger.error('Forced sign-out failed:', err);
        }
      }
    }, 60000); // Check every minute

    // Cleanup on unmount
    return () => {
      clearInterval(sessionCheckInterval);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <AccessibilityProvider>
            <LanguageProvider>
            <ToastProvider>
            {/* H5: writes userSessions + the screen-time rollup via a
                server-timed heartbeat. Must stay inside AuthProvider. */}
            <ActivityTracker />
            <div className="app-container">
              <SkipLinks />
              <GlobalShortcuts />
              <EmailVerificationBanner />
              <Suspense fallback={<LoadingSpinner />}>
                {/* `id="main-content"` added 2026-09-05: `SkipLinks` looks for
                    exactly this id (plus `navigation` and `chat`) and renders
                    nothing when it finds none — and NO element in the app had
                    any of them, so the skip links had never appeared. A skip
                    link is WCAG 2.4.1, and for a keyboard-only user it is the
                    difference between one keystroke and tabbing through the
                    whole header on every page. */}
                <main id="main-content" role="main" aria-label="Main content">
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/clever/callback" element={<CleverCallback />} />
                    <Route path="/signup" element={<Navigate to="/register" replace />} />
                    {/* #46 fix: first-time Google sign-up ToS capture */}
                    <Route path="/accept-terms" element={<AcceptTerms />} />
                    {/* S45: inviteTeacher emails a link here. This route did not
                        exist, so every teacher invitation landed on NotFound. */}
                    <Route
                      path="/teacher/accept-invite"
                      element={<AcceptTeacherInvite />}
                    />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/play/:publishId" element={<PlayGame />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/project/new"
                      element={
                        <ProtectedRoute>
                          <NewProject />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/project/:projectId"
                      element={
                        <ProtectedRoute>
                          <ProjectEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/achievements"
                      element={
                        <ProtectedRoute>
                          <Achievements />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/curriculum"
                      element={
                        <ProtectedRoute>
                          <CurriculumBrowser />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/challenges"
                      element={
                        <ProtectedRoute>
                          <ChallengesHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/challenges/:challengeId"
                      element={
                        <ProtectedRoute>
                          <ChallengePlay />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/glossary"
                      element={
                        <ProtectedRoute>
                          <GlossaryPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/snippets"
                      element={
                        <ProtectedRoute>
                          <SnippetLibrary />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 2: Teacher Routes */}
                    <Route
                      path="/teacher"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['teacher', 'admin']}>
                            <TeacherDashboard />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/classroom/:classroomId"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['teacher', 'admin']}>
                            <ClassroomDetail />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/grade/:assignmentId"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['teacher', 'admin']}>
                            <GradingView />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/lms"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['teacher', 'admin']}>
                            <FeatureGate feature="lmsIntegration">
                              <LMSIntegration />
                            </FeatureGate>
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 2: Admin Routes */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['admin']}>
                            <AdminDashboard />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 3: Student Progress & Assessment Routes */}
                    <Route
                      path="/skills"
                      element={
                        <ProtectedRoute>
                          <SkillMap />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/learning-path"
                      element={
                        <ProtectedRoute>
                          <MySkillsView />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/assessment/:assessmentId"
                      element={
                        <ProtectedRoute>
                          <AssessmentPlayer />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/report"
                      element={
                        <ProtectedRoute>
                          <StudentReport />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/report/:studentId"
                      element={
                        <ProtectedRoute>
                          <StudentReport />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 5: Community & Engagement Routes */}
                    <Route
                      path="/gallery"
                      element={
                        <ProtectedRoute>
                          <ProjectGallery />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reviews"
                      element={
                        <ProtectedRoute>
                          <PeerReviewHub />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/jams"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="gameJams">
                            <GameJamHub />
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/level"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="levelProgress">
                            <LevelProgress />
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/daily"
                      element={
                        <ProtectedRoute>
                          <DailyChallenge />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 11: Documentation, Training & Support */}
                    <Route
                      path="/training"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['teacher', 'admin']}>
                            <TeacherPDCourse />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/help"
                      element={
                        <ProtectedRoute>
                          <HelpCenter />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/lesson-plans"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['teacher', 'admin']}>
                            <LessonPlanLibrary />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/welcome" element={<GuidedOnboarding />} />
                    {/* Tier 12: Internationalization */}
                    <Route
                      path="/language"
                      element={
                        <ProtectedRoute>
                          <LanguageSelector />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 13: Cross-Curricular Integration */}
                    <Route
                      path="/math"
                      element={
                        <ProtectedRoute>
                          <MathVisualizer />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/physics"
                      element={
                        <ProtectedRoute>
                          <PhysicsLab />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/design"
                      element={
                        <ProtectedRoute>
                          <DesignStudio />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/story"
                      element={
                        <ProtectedRoute>
                          <StoryBuilder />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 14: Polish & UX */}
                    <Route
                      path="/notifications"
                      element={
                        <ProtectedRoute>
                          {/* S201: `variant="page"`. The route used to render the header
                              DROPDOWN, so a child who followed the bell arrived at a page
                              showing a single bell they had to click. */}
                          <NotificationCenter variant="page" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/present/:projectId"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="presentationMode">
                            <PresentationMode />
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/record"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="standaloneRecording">
                            <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 20px' }}>
                              <h1 style={{ color: 'var(--text-primary)', margin: '0 0 8px' }}>🎥 Record Your Game</h1>
                              <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px', lineHeight: 1.5 }}>
                                Capture a video of your game to share or submit. Open your game in another
                                tab, then click <strong>Record Screen</strong> below and choose that tab.
                                When you&apos;re done, stop the recording to download your clip.
                              </p>
                              <RecordingControls />
                            </div>
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 10: Platform & Infrastructure */}
                    <Route
                      path="/billing"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="billing">
                            <BillingDashboard />
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/tenants"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['admin']}>
                            <TenantManager />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/cms"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['admin']}>
                            <CMSEditor />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 9: Parental & Family Engagement */}
                    <Route
                      path="/parent"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['parent', 'admin']}>
                            <ParentDashboard />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/parent/screen-time"
                      element={
                        <ProtectedRoute>
                          <RoleGuard allowedRoles={['parent', 'admin']}>
                            <ScreenTimeControls />
                          </RoleGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/family"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="familyMode">
                            <RoleGuard allowedRoles={['parent', 'admin']}>
                              <FamilyMode />
                            </RoleGuard>
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 8: Advanced Capabilities */}
                    <Route
                      path="/phaser"
                      element={
                        <ProtectedRoute>
                          <PhaserWorkspace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/blocks"
                      element={
                        <ProtectedRoute>
                          <BlockEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/collaborate/:projectId"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="realtimeCollaboration">
                            <CollaborationPanel />
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/export/:projectId"
                      element={
                        <ProtectedRoute>
                          <ExportPanel />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/shortcuts"
                      element={
                        <ProtectedRoute>
                          <ShortcutSettings />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 7: Content & Asset Tools */}
                    <Route
                      path="/sprites"
                      element={
                        <ProtectedRoute>
                          <SpriteLibrary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sounds"
                      element={
                        <ProtectedRoute>
                          <SoundLibrary />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/sprite-editor"
                      element={
                        <ProtectedRoute>
                          <SpriteEditor />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/templates"
                      element={
                        <ProtectedRoute>
                          <GuidedTemplateBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/templates/:templateId"
                      element={
                        <ProtectedRoute>
                          <GuidedTemplateBuilder />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 6: Debugging & Development Tools */}
                    <Route
                      path="/project/:projectId/history"
                      element={
                        <ProtectedRoute>
                          <VersionHistoryWrapper />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 4: Safety, Compliance & Accessibility Routes */}
                    <Route path="/consent" element={<ParentalConsentFlow />} />
                    <Route path="/accessibility" element={<AccessibilityStatement />} />
                    <Route
                      path="/accessibility/settings"
                      element={
                        <ProtectedRoute>
                          <AccessibilityPanel />
                        </ProtectedRoute>
                      }
                    />
                    {/* Tier 2: Student Assignment Routes */}
                    <Route
                      path="/assignments"
                      element={
                        <ProtectedRoute>
                          <FeatureGate feature="studentAssignments">
                            <StudentAssignments />
                          </FeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/" element={<LandingPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </main>
              </Suspense>
              {/* Last in the DOM deliberately: this renders a floating button,
                  and the E2E keyboard walks assert that the first Tab from the
                  top of a page reaches the page's own controls. A utility that
                  is available everywhere must not be the first thing every
                  keyboard user meets, on every route. */}
              <KeyboardShortcutsOverlay />
              {/* Task 92. After the shortcuts overlay for the same reason it is after the
                  routes: a keyboard user's first Tab on any page must reach that page's own
                  controls, not a site-wide notice. It is a region rather than a modal, so
                  nothing is focus-trapped, and it RESERVES its own height (body padding and
                  scroll-padding, see CookieConsent.tsx) so the page behind it stays usable —
                  until 2026-10-05 it did not, and covered Send, Sign in and Delete My Account — a blocking dialog a
                  screen-reader user cannot escape is a WCAG finding, and the sale path runs
                  through a VPAT. Consent is DENIED until answered, so the notice being ignorable
                  is safe: ignoring it sends nothing. */}
              <CookieConsentBanner />
            </div>
            </ToastProvider>
            </LanguageProvider>
            </AccessibilityProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
